import { forwardRef, useContext, useEffect, useImperativeHandle, useRef, useState } from "react"
import ReactCrop from 'react-image-crop'
import 'react-image-crop/dist/ReactCrop.css'
import Utils from '@utils/utils'
import { AppContext } from '@utils/AppContext';
import { Spin } from "antd";
import { CloseCircleFilled } from '@ant-design/icons';

const MattingOpt = forwardRef((props, ref) => {
    const { setGlobalLoading } = useContext(AppContext);
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState();
    const [imgSrc, setImgSrc] = useState();
    const imgRef = useRef(null);
    const [loading, setLoading] = useState(false);

    useImperativeHandle(ref, () => ({
        saveHandle
    }))

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            try {
                // 加载原图
                if (props.inputImage) {
                    const base64 = await Utils.urlToBase64(props.inputImage);
                    setImgSrc(base64);
                }

                // 1. 优先从 rect 恢复选区 (直接使用保存的坐标信息)
                // 注意：具体的 crop 设置需要等待图片加载完成 (onImageLoad)

                // 2. 如果没有 rect，尝试从 whiteMask 恢复 (兼容旧数据)
                if (!props.rect && props.whiteMask) {
                    const maskBase64 = await Utils.urlToBase64(props.whiteMask);
                    const maskImg = new Image();
                    maskImg.crossOrigin = 'anonymous';
                    maskImg.onload = () => {
                        const canvas = document.createElement('canvas');
                        canvas.width = maskImg.width;
                        canvas.height = maskImg.height;
                        const ctx = canvas.getContext('2d');
                        ctx.drawImage(maskImg, 0, 0);

                        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
                        const data = imageData.data;
                        let minX = canvas.width, minY = canvas.height, maxX = 0, maxY = 0;
                        let found = false;

                        // 扫描白色区域
                        for (let y = 0; y < canvas.height; y++) {
                            for (let x = 0; x < canvas.width; x++) {
                                const i = (y * canvas.width + x) * 4;
                                if (data[i] > 128) {
                                    if (x < minX) minX = x;
                                    if (x > maxX) maxX = x;
                                    if (y < minY) minY = y;
                                    if (y > maxY) maxY = y;
                                    found = true;
                                }
                            }
                        }

                        if (found) {
                            const x = (minX / maskImg.width) * 100;
                            const y = (minY / maskImg.height) * 100;
                            const w = ((maxX - minX) / maskImg.width) * 100;
                            const h = ((maxY - minY) / maskImg.height) * 100;

                            setCrop({
                                unit: '%',
                                x,
                                y,
                                width: w,
                                height: h
                            });
                            // 设置 completedCrop 以便不移动也能直接保存
                            setCompletedCrop({
                                unit: '%',
                                x,
                                y,
                                width: w,
                                height: h
                            });
                        }
                    };
                    maskImg.src = maskBase64;
                }
            } catch (error) {
                console.error("Init MattingOpt error:", error);
            } finally {
                setLoading(false);
            }
        };

        init();
    }, [props.inputImage, props.whiteMask, props.rect]);

    // 图片加载完成后，如果有 rect，则设置初始选区
    const onImageLoad = (e) => {
        const { naturalWidth, naturalHeight } = e.currentTarget;
        if (props.rect) {
            const { x, y, width: w, height: h } = props.rect;
            // 将保存的像素坐标转换为百分比，适配当前显示尺寸
            const pX = (x / naturalWidth) * 100;
            const pY = (y / naturalHeight) * 100;
            const pW = (w / naturalWidth) * 100;
            const pH = (h / naturalHeight) * 100;

            const newCrop = {
                unit: '%',
                x: pX,
                y: pY,
                width: pW,
                height: pH
            };
            setCrop(newCrop);
            setCompletedCrop(newCrop);
        }
    };

    const saveHandle = async () => {
        if (!completedCrop || !imgRef.current) {
             return {
                blueMask: null,
                whiteMask: null,
                blendedImage: null,
                rect: null
            }
        }

        setLoading(true);
        try {
            const image = imgRef.current;
            let x, y, width, height;

            // 处理 crop 单位 (px 或 %)
            if (completedCrop.unit === '%') {
                x = (completedCrop.x / 100) * image.naturalWidth;
                y = (completedCrop.y / 100) * image.naturalHeight;
                width = (completedCrop.width / 100) * image.naturalWidth;
                height = (completedCrop.height / 100) * image.naturalHeight;
            } else {
                const scaleX = image.naturalWidth / image.width;
                const scaleY = image.naturalHeight / image.height;
                x = completedCrop.x * scaleX;
                y = completedCrop.y * scaleY;
                width = completedCrop.width * scaleX;
                height = completedCrop.height * scaleY;
            }

            // 创建mask canvas
            const canvas = document.createElement('canvas');
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
            const ctx = canvas.getContext('2d');

            // 1. 生成 whiteMask (黑底白框)
            ctx.fillStyle = 'black';
            ctx.fillRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = 'white';
            ctx.fillRect(x, y, width, height);
            const whiteMaskBase64 = canvas.toDataURL('image/png');

            // 2. 生成 blueMask (透明底蓝框)
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.fillStyle = 'rgba(0, 110, 255, 0.3)';
            ctx.fillRect(x, y, width, height);
            const blueMaskBase64 = canvas.toDataURL('image/png');

            const [blueRes, whiteRes] = await Promise.all([
                Utils.uploadBase64Image(blueMaskBase64),
                Utils.uploadBase64Image(whiteMaskBase64),
            ]);

            if (blueRes && whiteRes) {
                return {
                    blueMask: blueRes,
                    whiteMask: whiteRes,
                    blendedImage: null,
                    rect: { x, y, width, height }
                }
            } else {
                return false;
            }

        } catch (error) {
            console.error("Save handle error:", error);
            return false;
        } finally {
            setLoading(false);
        }
    }

    const handleDelete = (e) => {
        e.stopPropagation();
        e.preventDefault();
        setCrop(undefined);
        setCompletedCrop(undefined);
    }

    return (
        <Spin spinning={loading}>
            <div style={{ width: 468, margin: '0 auto', position: 'relative' }}>
                <p style={{ fontSize: 14, color: '#333', margin: '20px 0' }}>请在图中框选要去除的内容，如果没有请直接点击【完成】按钮</p>
                <style>{`
                    .custom-crop .ReactCrop__crop-selection {
                        background: rgba(0, 110, 255, 0.3);
                        border: 1px solid #006EFF;
                        box-shadow: none !important;
                    }
                    .ReactCrop__drag-handle {
                         width: 10px;
                         height: 10px;
                         background-color: #006EFF;
                         border: 1px solid rgba(255,255,255,0.7);
                    }
                `}</style>
                {imgSrc && (
                    <ReactCrop
                        crop={crop}
                        onChange={(c) => setCrop(c)}
                        onComplete={(c) => setCompletedCrop(c)}
                        keepSelection={true}
                        className="custom-crop"
                        renderSelectionAddon={() => {
                            return (
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: -10,
                                        right: -10,
                                        cursor: 'pointer',
                                        zIndex: 100
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()} 
                                    onClick={handleDelete}
                                >
                                    <CloseCircleFilled style={{ fontSize: '20px', color: 'red', background: 'white', borderRadius: '50%' }} />
                                </div>
                            )
                        }}
                    >
                        <img
                            ref={imgRef}
                            src={imgSrc}
                            onLoad={onImageLoad}
                            style={{ width: '100%', display: 'block' }}
                            alt="Input"
                        />
                    </ReactCrop>
                )}
            </div>
        </Spin>
    )
})

export default MattingOpt